import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, Btn, Field, Input } from "@/components/admin/ui";
import { API } from "@/config";
import { formatSlot } from "@/utils/slotLabel";
import { Calendar as CalIcon, CheckCircle, ExternalLink, Link2Off, ChevronDown, ChevronUp, RefreshCw, Zap, X, Package, Layers, Printer } from "lucide-react";

/* ── OrderDetailModal ─────────────────────────────────────────────────── */
const statusColors = {
  confirmed: "text-emerald-400 bg-emerald-500/15 border-emerald-500/40",
  assigned:  "text-blue-400 bg-blue-500/15 border-blue-500/40",
  in_progress:"text-amber-400 bg-amber-500/15 border-amber-500/40",
  completed: "text-[#B9C8D5] bg-white/8 border-white/20",
};

const OrderDetailModal = ({ order: o, onClose }) => {
  if (!o) return null;
  const statusCls = statusColors[o.status] || "text-[#B9C8D5] bg-white/8 border-white/20";
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="cal-order-modal"
    >
      <div className="w-full max-w-md bg-[#0A1825] border border-[#FFD166]/30 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-[#FFD166]/15 flex-shrink-0">
          <div>
            <div className="font-semibold text-[#F6F1E4] text-lg leading-tight">{o.customer_name}</div>
            <div className="font-mono text-[#FFD166] text-xs mt-0.5">#{o.id.slice(0, 8).toUpperCase()}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${statusCls}`}>
              {o.status?.replace("_", " ")}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-[#B9C8D5] hover:text-white hover:bg-white/10 transition-colors"
              data-testid="cal-order-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-black/30 rounded-xl p-3 border border-[#FFD166]/12">
              <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-1">Delivery</div>
              <div className="text-sm font-medium text-[#F6F1E4]">{o.delivery_date || "—"}</div>
              {o.delivery_slot && <div className="text-xs text-[#FFD166] mt-0.5">{formatSlot(o.delivery_slot)}</div>}
            </div>
            <div className="bg-black/30 rounded-xl p-3 border border-[#FFD166]/12">
              <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-1">Pickup</div>
              <div className="text-sm font-medium text-[#F6F1E4]">{o.pickup_date || "—"}</div>
              {o.pickup_slot && <div className="text-xs text-[#FFD166] mt-0.5">{formatSlot(o.pickup_slot)}</div>}
            </div>
          </div>

          {/* location */}
          {o.delivery_resort && (
            <div className="bg-black/30 rounded-xl p-3 border border-[#FFD166]/12">
              <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-1">Location</div>
              <div className="text-sm text-[#F6F1E4]">{o.delivery_resort}</div>
            </div>
          )}

          {/* gear */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-2">Gear</div>
            <div className="space-y-1.5">
              {(o.line_items || []).map((li, i) => (
                <div key={i} className="flex flex-col bg-black/30 rounded-lg px-3 py-2 border border-[#FFD166]/10">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#F6F1E4] leading-snug">{li.name}</span>
                    <span className="text-sm text-[#FFD166] font-semibold ml-3 flex-shrink-0">× {li.quantity}</span>
                  </div>
                  {(li.staged_serials || []).some(Boolean) && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {Array.from({ length: li.quantity }).map((_, idx) => {
                        const serial = (li.staged_serials || [])[idx];
                        return serial ? (
                          <span key={idx} className="inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full font-mono">
                            ✓ {serial}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              ))}
              {(!o.line_items || o.line_items.length === 0) && (
                <div className="text-sm text-[#B9C8D5]">No items</div>
              )}
            </div>
          </div>

          {/* total */}
          <div className="flex justify-between items-center pt-1 border-t border-[#FFD166]/15">
            <span className="text-sm text-[#B9C8D5]">Total</span>
            <span className="text-lg font-serif text-[#FFD166]">${Number(o.total || 0).toFixed(2)}</span>
          </div>
        </div>

        {/* footer */}
        <div className="px-5 py-4 border-t border-[#FFD166]/15 flex-shrink-0">
          <a
            href={`/admin/reservations?open=${o.id}`}
            className="block w-full text-center text-sm text-[#B9C8D5] hover:text-[#FFD166] transition-colors"
            data-testid="cal-order-modal-full-link"
          >
            View full reservation →
          </a>
        </div>
      </div>
    </div>
  );
};

/* ── InventoryModal ───────────────────────────────────────────────────── */
const InventoryModal = ({ list, date, mode, onClose }) => {
  const agg = list.reduce((acc, o) => {
    (o.line_items || []).forEach(li => {
      acc[li.name] = (acc[li.name] || 0) + (li.quantity || 1);
    });
    return acc;
  }, {});
  const items = Object.entries(agg).sort((a, b) => b[1] - a[1]);
  const total = items.reduce((s, [, q]) => s + q, 0);
  const modeLabel = mode === "deliveries" ? "Going Out" : "Coming Back";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="cal-inventory-modal"
    >
      <div className="w-full max-w-sm bg-[#0A1825] border border-[#FFD166]/30 rounded-2xl shadow-2xl max-h-[80vh] flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#FFD166]/15 flex-shrink-0">
          <div>
            <div className="font-semibold text-[#F6F1E4]">Inventory {modeLabel}</div>
            <div className="text-xs text-[#B9C8D5] mt-0.5">{date} · {total} unit{total !== 1 ? "s" : ""} total</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#B9C8D5] hover:text-white hover:bg-white/10 transition-colors"
            data-testid="cal-inventory-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* list */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-2">
          {items.length === 0 && (
            <div className="text-sm text-[#B9C8D5] text-center py-4">Nothing scheduled.</div>
          )}
          {items.map(([name, qty]) => (
            <div key={name} className="flex justify-between items-center bg-black/30 rounded-xl px-4 py-3 border border-[#FFD166]/12">
              <div className="flex items-center gap-2.5">
                <Package className="w-3.5 h-3.5 text-[#FFD166]/60 flex-shrink-0" />
                <span className="text-sm text-[#F6F1E4] leading-snug">{name}</span>
              </div>
              <span className="font-serif text-xl text-[#FFD166] ml-3 flex-shrink-0">× {qty}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Compact Google Calendar connect panel ─────────────────────────── */
const GoogleCalendarPanel = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [creds, setCreds] = useState({ client_id: "", client_secret: "" });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/calendar-settings/status`);
      setStatus(r.data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    load();
    if (params.get("connected") === "1") { toast.success("Google Calendar connected!"); setExpanded(true); }
    if (params.get("error")) toast.error(`Google Calendar: ${params.get("error")}`);
  }, [load, params]);

  const saveCreds = async () => {
    if (!creds.client_id.trim() || !creds.client_secret.trim())
      return toast.error("Enter both Client ID and Client Secret");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/calendar-settings/credentials`, creds);
      toast.success("Credentials saved — click Connect to authorize");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/admin/calendar-settings/connect`);
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start authorization");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    await axios.delete(`${API}/admin/calendar-settings/disconnect`);
    toast.success("Disconnected"); load();
  };

  const syncAll = async (force = false) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/calendar-settings/sync-all?force=${force}`);
      setSyncResult(data);
      if (data.synced > 0) {
        toast.success(`Synced ${data.synced} booking${data.synced !== 1 ? "s" : ""} to Google Calendar`);
      } else if (data.skipped === data.total) {
        toast.success(`All ${data.total} booking${data.total !== 1 ? "s" : ""} already on calendar — nothing to do`);
      } else {
        toast.info(`Done — ${data.synced} synced, ${data.skipped} already existed, ${data.failed} failed`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (!status) return null;

  return (
    <div className="mt-6 rounded-xl border border-[#FFD166]/20 bg-[#0A1825] overflow-hidden" data-testid="gcal-panel">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-black/20 transition-colors"
        data-testid="gcal-panel-toggle"
      >
        <div className="flex items-center gap-3">
          <CalIcon className="w-4 h-4 text-[#FFD166]" />
          <span className="text-sm font-medium text-[#F6F1E4]">Google Calendar Sync</span>
          {status.connected ? (
            <span className="flex items-center gap-1 text-xs text-emerald-400">
              <CheckCircle className="w-3 h-3" /> Connected{status.connected_email ? ` · ${status.connected_email}` : ""}
            </span>
          ) : (
            <span className="text-xs text-[#B9C8D5]/60">Not connected</span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#B9C8D5]" /> : <ChevronDown className="w-4 h-4 text-[#B9C8D5]" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-5 border-t border-[#FFD166]/10">
          {status.connected ? (
            <div className="pt-4 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-sm text-[#F6F1E4] font-medium">Connected as {status.connected_email}</div>
                  <div className="text-xs text-[#B9C8D5]">New bookings automatically create Delivery + Pickup calendar events.</div>
                </div>
                <Btn variant="ghost" onClick={disconnect} data-testid="gcal-disconnect-btn">
                  <Link2Off className="w-4 h-4 mr-1.5" /> Disconnect
                </Btn>
              </div>
              <div className="border-t border-[#FFD166]/10 pt-4">
                <div className="text-xs font-medium text-[#B9C8D5] mb-3 uppercase tracking-wider">Sync Bookings to Calendar</div>
                <div className="flex flex-wrap gap-2">
                  <Btn
                    onClick={() => syncAll(false)}
                    disabled={syncing}
                    data-testid="gcal-sync-missing-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                    {syncing ? "Syncing…" : "Sync missing bookings"}
                  </Btn>
                  <Btn
                    variant="ghost"
                    onClick={() => { if (window.confirm("Re-sync ALL paid bookings? This will delete existing calendar events and recreate them. Continue?")) syncAll(true); }}
                    disabled={syncing}
                    data-testid="gcal-sync-all-btn"
                  >
                    <Zap className="w-4 h-4 mr-1.5" />
                    Force re-sync all
                  </Btn>
                </div>
                {syncResult && (
                  <div className="mt-3 text-xs rounded-lg bg-black/30 border border-[#FFD166]/15 px-3 py-2 text-[#B9C8D5] space-y-0.5" data-testid="gcal-sync-result">
                    <div>Total paid bookings: <strong className="text-[#F6F1E4]">{syncResult.total}</strong></div>
                    <div>Newly synced: <strong className="text-emerald-400">{syncResult.synced}</strong></div>
                    <div>Already on calendar: <strong className="text-[#F6F1E4]">{syncResult.skipped}</strong></div>
                    {syncResult.failed > 0 && <div>Failed: <strong className="text-red-400">{syncResult.failed}</strong></div>}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {!status.has_credentials && (
                <div className="pt-4 space-y-4">
                  <p className="text-xs text-[#B9C8D5] leading-relaxed">
                    To connect, you need a Google Cloud OAuth Client ID + Secret with the Calendar API enabled.{" "}
                    <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#FFD166] underline inline-flex items-center gap-0.5">console.cloud.google.com <ExternalLink className="w-2.5 h-2.5"/></a>
                  </p>
                  <div className="text-xs text-[#B9C8D5] space-y-1">
                    <div>Add these Authorized redirect URIs in your Google Cloud OAuth client:</div>
                    <code className="block bg-black/30 px-2 py-1 rounded break-all">https://enchantedgearrentals.com/api/admin/calendar-settings/oauth/callback</code>
                    <code className="block bg-black/30 px-2 py-1 rounded break-all">https://driver-scan-hub-1.preview.emergentagent.com/api/admin/calendar-settings/oauth/callback</code>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Client ID">
                      <Input placeholder="xxxx.apps.googleusercontent.com" value={creds.client_id} onChange={e => setCreds({...creds, client_id: e.target.value})} data-testid="gcal-client-id-input" />
                    </Field>
                    <Field label="Client Secret">
                      <Input type="password" placeholder="GOCSPX-..." value={creds.client_secret} onChange={e => setCreds({...creds, client_secret: e.target.value})} data-testid="gcal-client-secret-input" />
                    </Field>
                  </div>
                  <Btn onClick={saveCreds} disabled={saving} data-testid="gcal-save-creds-btn">
                    {saving ? "Saving…" : "Save Credentials"}
                  </Btn>
                </div>
              )}
              {status.has_credentials && (
                <div className="pt-4">
                  <p className="text-xs text-[#B9C8D5] mb-3">Credentials saved. Authorize calendar access to finish setup.</p>
                  <Btn onClick={connect} disabled={connecting} data-testid="gcal-connect-btn">
                    <CalIcon className="w-4 h-4 mr-2" />
                    {connecting ? "Redirecting to Google…" : "Connect Google Calendar"}
                  </Btn>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const Calendar = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ days: {} });
  const [selDay, setSelDay] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [inventoryOpen, setInventoryOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/calendar?year=${year}&month=${month}`).then(r => setData(r.data));
    setSelDay(null); setShowAll(false);
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const shift = (delta) => {
    const cur = new Date(year, month - 1 + delta, 1);
    const min = new Date(now.getFullYear(), now.getMonth(), 1);
    const max = new Date(now.getFullYear() + 2, now.getMonth(), 1);
    if (cur < min || cur > max) return;
    setYear(cur.getFullYear()); setMonth(cur.getMonth() + 1);
  };

  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dateKey = (d) => `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const monthLabel = new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });

  const aggregate = (list) => {
    const map = {};
    list.forEach(o => (o.line_items || []).forEach(li => {
      const k = li.product_id;
      map[k] = { name: li.name, quantity: (map[k]?.quantity || 0) + (li.quantity || 1) };
    }));
    return Object.entries(map).map(([id, v]) => ({ id, ...v }));
  };

  const dayInfo = selDay ? (data.days[selDay.date] || { deliveries: [], pickups: [] }) : null;
  const selList = dayInfo ? dayInfo[selDay.mode] : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-4xl">Reservation Calendar</h1>
        <div className="flex items-center gap-3">
          <Btn variant="ghost" onClick={() => shift(-1)} data-testid="cal-prev">← Prev</Btn>
          <div className="font-serif text-2xl text-[#FFD166] min-w-[220px] text-center" data-testid="cal-month-label">{monthLabel}</div>
          <Btn variant="ghost" onClick={() => shift(1)} data-testid="cal-next">Next →</Btn>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <Card className="p-4">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.2em] text-[#B9C8D5] mb-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} className="aspect-square" />;
              const key = dateKey(d);
              const info = data.days[key] || { deliveries: [], pickups: [] };
              const nD = info.deliveries.length, nP = info.pickups.length;
              const isToday = key === new Date().toISOString().slice(0,10);
              const isSelected = selDay?.date === key;
              return (
                <div key={key} className={`aspect-square rounded-lg border p-1.5 text-xs flex flex-col transition-all duration-150 ${
                  isSelected
                    ? "border-[#FFD166] bg-[#FFD166]/12 shadow-[0_0_0_1px_rgba(255,209,102,0.25)]"
                    : isToday
                    ? "border-[#FFD166]/60 bg-black/25"
                    : "border-[#FFD166]/15 bg-black/25"
                }`} data-testid={`cal-day-${d}`}>
                  <div className="text-[#F6F1E4]">{d}</div>
                  <div className="flex-1 mt-1 flex flex-col justify-end gap-1">
                    {nD > 0 && <button onClick={() => { setSelDay({ date: key, mode: "deliveries" }); setShowAll(false); setInventoryOpen(false); }} className={`text-emerald-300 border rounded px-1.5 py-0.5 text-[11px] transition-colors ${isSelected && selDay?.mode === "deliveries" ? "bg-emerald-500/35 border-emerald-400/70" : "bg-emerald-500/15 border-emerald-500/40 hover:bg-emerald-500/25"}`} data-testid={`cal-day-${d}-deliv`}>🚚 {nD}</button>}
                    {nP > 0 && <button onClick={() => { setSelDay({ date: key, mode: "pickups" }); setShowAll(false); setInventoryOpen(false); }} className={`text-red-300 border rounded px-1.5 py-0.5 text-[11px] transition-colors ${isSelected && selDay?.mode === "pickups" ? "bg-red-500/35 border-red-400/70" : "bg-red-500/15 border-red-500/40 hover:bg-red-500/25"}`} data-testid={`cal-day-${d}-pick`}>🔄 {nP}</button>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-6 text-xs text-[#B9C8D5]">
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-500/40 border border-emerald-500/60 inline-block"></span> Deliveries</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-500/40 border border-red-500/60 inline-block"></span> Pickups / returns</span>
          </div>
        </Card>

        <div>
          {!selDay && <Card className="p-6 text-center text-[#B9C8D5]">Click a green or red badge on any day to see orders.</Card>}
          {selDay && (
            <Card className="p-5" data-testid="cal-day-detail">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="eyebrow">{selDay.mode === "deliveries" ? "Deliveries" : "Pickups / returns"}</div>
                  <div className="font-serif text-2xl text-[#FFD166]">{selDay.date}</div>
                </div>
                <Btn variant="ghost" onClick={() => setShowAll(!showAll)} data-testid="cal-view-all">{showAll ? "By order" : "View all items"}</Btn>
              </div>

              {/* Inventory going out button */}
              {selList.length > 0 && (
                <button
                  onClick={() => setInventoryOpen(true)}
                  className="w-full mb-2 flex items-center justify-center gap-2 text-sm font-medium py-2.5 rounded-xl border border-[#FFD166]/35 text-[#FFD166] bg-[#FFD166]/6 hover:bg-[#FFD166]/14 transition-colors"
                  data-testid="cal-inventory-btn"
                >
                  <Layers className="w-4 h-4" />
                  View all inventory {selDay.mode === "deliveries" ? "going out" : "coming back"} this day
                </button>
              )}

              {/* Print run sheet */}
              <a
                href={`/admin/run-sheet?day=${selDay.date}`}
                target="_blank"
                rel="noreferrer"
                className="w-full mb-4 flex items-center justify-center gap-2 text-sm font-medium py-2.5 rounded-xl border border-[#B9C8D5]/30 text-[#B9C8D5] bg-white/4 hover:bg-white/8 hover:text-[#F6F1E4] transition-colors"
                data-testid="cal-print-runsheet-btn"
              >
                <Printer className="w-4 h-4" />
                Print run sheet for this day
              </a>

              {!showAll ? (
                <div className="space-y-2 max-h-[420px] overflow-auto">
                  {selList.length === 0 && <div className="text-sm text-[#B9C8D5] text-center py-4">Nothing scheduled.</div>}
                  {selList.map(o => {
                    const slot = selDay.mode === "deliveries" ? o.delivery_slot : o.pickup_slot;
                    return (
                    <button key={o.id} onClick={() => setSelectedOrder(o)} className="w-full text-left bg-black/30 border border-[#FFD166]/15 hover:border-[#FFD166]/60 rounded-lg p-3 transition-colors" data-testid={`cal-order-${o.id}`}>
                      <div className="flex justify-between text-sm">
                        <span className="font-mono text-[#FFD166]">#{o.id.slice(0, 8)}</span>
                        <span className="text-[#FFD166]">${Number(o.total).toFixed(2)}</span>
                      </div>
                      <div className="text-sm mt-1">{o.customer_name}</div>
                      <div className="text-xs text-[#B9C8D5]">{o.delivery_resort}</div>
                      {slot && <div className="text-xs text-[#FFD166] mt-1" data-testid={`cal-order-${o.id}-slot`}>{formatSlot(slot)}</div>}
                      <div className="text-xs text-[#B9C8D5] mt-1">{o.line_items?.map(li => `× ${li.quantity} ${li.name}`).join(" · ")}</div>
                    </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2" data-testid="cal-aggregate">
                  {aggregate(selList).map(a => (
                    <div key={a.id} className="flex justify-between bg-black/30 border border-[#FFD166]/15 rounded-lg p-3 text-sm">
                      <span className="text-[#F6F1E4]">{a.name}</span>
                      <span className="text-[#FFD166] font-serif text-lg">× {a.quantity}</span>
                    </div>
                  ))}
                  {aggregate(selList).length === 0 && <div className="text-sm text-[#B9C8D5] text-center py-4">Nothing scheduled.</div>}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
      <GoogleCalendarPanel />

      {/* Reservation detail modal */}
      <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />

      {/* Inventory modal */}
      {inventoryOpen && selDay && (
        <InventoryModal
          list={selList}
          date={selDay.date}
          mode={selDay.mode}
          onClose={() => setInventoryOpen(false)}
        />
      )}
    </div>
  );
};
